import { useContext, useEffect, useState } from "react";
import { useFormik } from "formik";
import {
  StepperContext,
  useLoanApplication,
  useLoanCalculator,
} from "../../../contexts";
import {
  UploadInvoiceFields,
  LOAN_UPLOAD_INVOICE_INITIAL_VALUES,
  LOAN_UPLOAD_INVOICE_VALIDATION_SCHEMA,
  handleReceivablesFinanceLoanUpdate,
  handlePayablesFinanceLoanUpdate,
} from "../../LoanCalculator/service";
import { useLoanMetrics, useMedia } from "../../../hooks";
import { LoanProductType } from "../../../hooks/api/types";
import UploadInvoiceForm from "./UploadInvoiceForm";
import { useDpConfig } from "../../../contexts/dpConfig";

const LoanUploadInvoice = ({ closeModal }: { closeModal: () => void }) => {
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [repayableByDP, setRepayableByDP] = useState(false);
  const [
    invoiceDiscountingRepayableByDPFlag,
    setInvoiceDiscountingRepayableByDPFlag,
  ] = useState(false);
  const [
    payableFinancingRepayableByDPFlag,
    setPayableFinancingRepayableByDPFlag,
  ] = useState(false);

  const {
    state: { id, invoiceFiles, loanType },
    actions: { update },
  } = useLoanApplication();

  const { state: calculatorState } = useLoanCalculator();
  const {
    state: {
      settings: { moveLoanCalculatorAtStart },
    },
  } = useDpConfig();

  const { upload } = useMedia();

  const { previousStep } = useContext(StepperContext);

  const { invoiceFinanceProduct, payableFinancingProduct } = useLoanMetrics();

  const {
    state: { payableFinancingLoan, invoiceDiscountingLoan },
    actions: { setPayableFinancingLoan, setInvoiceDiscountingLoan },
  } = useLoanCalculator();

  useEffect(() => {
    setInvoiceDiscountingRepayableByDPFlag(
      invoiceFinanceProduct?.flags?.repayableByDP
    );
    setPayableFinancingRepayableByDPFlag(
      payableFinancingProduct?.flags?.repayableByDP
    );
  }, [invoiceFinanceProduct, payableFinancingProduct]);

  useEffect(() => {
    setRepayableByDP(
      invoiceDiscountingRepayableByDPFlag || payableFinancingRepayableByDPFlag
    );
  }, [invoiceDiscountingRepayableByDPFlag, payableFinancingRepayableByDPFlag]);

  async function updateLoanApplication() {
    if (calculatorState.type === LoanProductType.INVOICE_DISCOUNTING) {
      await handleReceivablesFinanceLoanUpdate(
        id,
        invoiceFiles,
        calculatorState,
        update,
        upload,
        setLoading
      );
    } else if (calculatorState.type === LoanProductType.PAYABLE_FINANCING) {
      await handlePayablesFinanceLoanUpdate(
        id,
        invoiceFiles,
        calculatorState,
        update,
        upload,
        setLoading
      );
    }
  }

  useEffect(() => {
    //only run if in LOC flow mode
    if (!moveLoanCalculatorAtStart) {
      if (invoiceFiles.length) {
        setLoading(true);
        updateLoanApplication()
          .then(async () => {})
          .finally(() => {
            setLoading(false);
          });
      }
    }
  }, [invoiceFiles, moveLoanCalculatorAtStart]);

  const formik = useFormik<UploadInvoiceFields>({
    initialValues: {
      ...LOAN_UPLOAD_INVOICE_INITIAL_VALUES,
      invoiceAmount:
        loanType === LoanProductType.PAYABLE_FINANCING
          ? payableFinancingLoan.invoices[0]?.amount
          : invoiceDiscountingLoan.invoices[0]?.amount,
    },
    validationSchema: LOAN_UPLOAD_INVOICE_VALIDATION_SCHEMA,
    onSubmit: async (values) => {
      if (loanType === LoanProductType.PAYABLE_FINANCING) {
        setPayableFinancingLoan({
          invoices: payableFinancingLoan.invoices.map((invoice) => {
            return {
              ...invoice,
              invoiceDueDate: values.invoiceDueDate
                ? values.invoiceDueDate
                : new Date(),
              invoiceIssuedDate: values.invoiceIssuedDate
                ? values.invoiceIssuedDate
                : new Date(),
              invoiceNo: values.invoiceNo ? values.invoiceNo : "",
              vendorName: values.vendorName,
            };
          }),
        });
      }
      if (loanType === LoanProductType.INVOICE_DISCOUNTING) {
        setInvoiceDiscountingLoan({
          invoices: invoiceDiscountingLoan.invoices.map((invoice) => {
            return {
              ...invoice,
              invoiceDueDate: values.invoiceDueDate
                ? values.invoiceDueDate
                : new Date(),
              invoiceIssuedDate: values.invoiceIssuedDate
                ? values.invoiceIssuedDate
                : new Date(),
              invoiceNo: values.invoiceNo ? values.invoiceNo : "",
              vendorName: values.vendorName,
            };
          }),
        });
      }
      try {
        await update(
          {
            loanType: calculatorState.type,
            invoiceFiles: values.invoices,
          },
          { local: true }
        );
        closeModal();
      } catch (err) {
        console.log(err);
      }
    },
  });

  const handleUpload = (file: File) => {
    setUploading(true);
    formik.setFieldValue("invoices", [file]);
    setUploading(false);
  };

  return (
    <div className="cx-grow cx-flex cx-flex-col cx-items-center cx-justify-center cx-w-full">
      <div className="cx-text-text-secondary cx-text-center">
        Please note the invoicing party must be the same as the entity applying
        for the loan, and all invoices must be in AED.
      </div>
      <UploadInvoiceForm
        formik={formik}
        handleUpload={handleUpload}
        uploading={uploading || loading}
        repayableByDP={repayableByDP}
        previousStep={previousStep}
        onReviewScreen={false}
      />
    </div>
  );
};

export default LoanUploadInvoice;
